import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bookingsAPI, servicesAPI } from '../utils/api';
import AdminBookingModal from '../components/AdminBookingModal';
import CustomerBookingHistoryTable from '../components/CustomerBookingHistoryTable';
import CustomerBookingTimeline from '../components/CustomerBookingTimeline';
import {
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  Box,
  Chip,
} from '@mui/material';
import TableRowsIcon from '@mui/icons-material/TableRows';
import TimelineIcon from '@mui/icons-material/Timeline';
import CreditCardIcon from '@mui/icons-material/CreditCard';

function CustomerHistory() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(isMobile ? 'timeline' : 'table');

  // Admin notes
  const [editingNotes, setEditingNotes] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [preferences, setPreferences] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Rebook modal
  const [showRebookModal, setShowRebookModal] = useState(false);
  const [rebookService, setRebookService] = useState(null);
  const [services, setServices] = useState([]);

  // Get customer initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  useEffect(() => {
    fetchCustomerHistory();
    fetchServices();
  }, [customerId]);

  const fetchCustomerHistory = async () => {
    setLoading(true);
    try {
      const response = await bookingsAPI.getCustomerHistory(customerId);
      setData(response.data);
      setAdminNotes(response.data.customer.admin_notes || '');
      setPreferences(response.data.customer.preferences || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load customer history');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await bookingsAPI.updateCustomerNotes(customerId, { admin_notes: adminNotes, preferences });
      setEditingNotes(false);
      toast.success('Notes saved successfully');
      fetchCustomerHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleRebook = (booking) => {
    const service = services.find(s => s.name === booking.service_name) || {
      id: booking.service_id,
      name: booking.service_name,
      price: booking.service_price,
      duration: booking.service_duration || 60
    };
    setRebookService(service);
    setShowRebookModal(true);
  };

  const handleBookingSuccess = () => {
    fetchCustomerHistory();
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading customer history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/admin')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const { customer, bookings, stats } = data;

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <button className="btn btn-secondary" onClick={() => navigate('/admin')}>
          Back to Dashboard
        </button>
        <button className="btn btn-primary" onClick={() => {
          setRebookService(null);
          setShowRebookModal(true);
        }}>
          + New Booking
        </button>
      </div>

      <h1 style={{ marginBottom: '2rem' }}>Customer History</h1>

      {/* Customer Info Card */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              customer.stripe_customer_id ? (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                  }}
                >
                  <CreditCardIcon sx={{ fontSize: 14, color: 'white' }} />
                </Box>
              ) : null
            }
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                fontSize: '1.5rem',
                bgcolor: 'primary.main',
              }}
            >
              {getInitials(customer.name)}
            </Avatar>
          </Badge>
          <Box>
            <h2 style={{ margin: 0 }}>{customer.name}</h2>
            {customer.stripe_customer_id && (
              <Chip
                label="Card on file"
                color="success"
                size="small"
                icon={<CreditCardIcon />}
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
        </Box>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
          <div>
            <p style={{ color: '#666', marginBottom: '0.25rem' }}>Email</p>
            <p style={{ fontWeight: 'bold' }}>{customer.email}</p>
          </div>
          <div>
            <p style={{ color: '#666', marginBottom: '0.25rem' }}>Phone</p>
            <p style={{ fontWeight: 'bold' }}>{customer.phone || 'Not provided'}</p>
          </div>
          <div>
            <p style={{ color: '#666', marginBottom: '0.25rem' }}>Customer Since</p>
            <p style={{ fontWeight: 'bold' }}>{new Date(customer.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Admin Notes */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Admin Notes</h3>
          {!editingNotes ? (
            <button className="btn btn-secondary" style={{ padding: '0.35rem 0.75rem' }} onClick={() => setEditingNotes(true)}>
              Edit
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary" style={{ padding: '0.35rem 0.75rem' }} onClick={handleSaveNotes} disabled={savingNotes}>
                {savingNotes ? 'Saving...' : 'Save'}
              </button>
              <button className="btn btn-secondary" style={{ padding: '0.35rem 0.75rem' }} onClick={() => {
                setEditingNotes(false);
                setAdminNotes(customer.admin_notes || '');
                setPreferences(customer.preferences || '');
              }}>
                Cancel
              </button>
            </div>
          )}
        </div>

        {editingNotes ? (
          <div>
            <div className="form-group">
              <label>Notes (internal)</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Internal notes about this customer..."
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Preferences</label>
              <textarea
                value={preferences}
                onChange={(e) => setPreferences(e.target.value)}
                placeholder="Customer preferences (e.g., prefers morning appointments, allergies, etc.)"
                rows={2}
              />
            </div>
          </div>
        ) : (
          <div>
            {adminNotes || preferences ? (
              <>
                {adminNotes && (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Notes:</strong> {adminNotes}
                  </div>
                )}
                {preferences && (
                  <div>
                    <strong>Preferences:</strong> {preferences}
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic' }}>No notes yet. Click Edit to add notes about this customer.</p>
            )}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="stats-grid" style={{ marginBottom: '2rem' }}>
        <div className="stat-card">
          <h3>Total Visits</h3>
          <div className="value">{stats.completed_bookings}</div>
        </div>
        <div className="stat-card">
          <h3>Total Spent</h3>
          <div className="value" style={{ color: '#10B981' }}>£{stats.total_spent.toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <h3>Favorite Service</h3>
          <div className="value" style={{ fontSize: '1.25rem' }}>{stats.favorite_service || 'N/A'}</div>
        </div>
        <div className="stat-card">
          <h3>Cancellations</h3>
          <div className="value" style={{ color: stats.cancelled_bookings > 0 ? '#dc3545' : '#28a745' }}>
            {stats.cancelled_bookings}
          </div>
        </div>
      </div>

      {/* Booking History */}
      <div className="card">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <h2 style={{ margin: 0 }}>Booking History</h2>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newValue) => newValue && setViewMode(newValue)}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="table" aria-label="table view">
              <TableRowsIcon sx={{ mr: 0.5 }} />
              Table
            </ToggleButton>
            <ToggleButton value="timeline" aria-label="timeline view">
              <TimelineIcon sx={{ mr: 0.5 }} />
              Timeline
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {stats.first_visit && (
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            First visit: {stats.first_visit} | Last visit: {stats.last_visit}
          </p>
        )}

        {viewMode === 'table' ? (
          <CustomerBookingHistoryTable
            bookings={bookings}
            onRebook={handleRebook}
          />
        ) : (
          <CustomerBookingTimeline
            bookings={bookings}
            onRebook={handleRebook}
          />
        )}
      </div>

      {/* Admin Booking Modal */}
      <AdminBookingModal
        isOpen={showRebookModal}
        onClose={() => setShowRebookModal(false)}
        onSuccess={handleBookingSuccess}
        preSelectedCustomer={customer}
        preSelectedService={rebookService}
      />
    </div>
  );
}

export default CustomerHistory;
